"use client";

/**
 * Глобальный confirm-диалог. Заменяет `window.confirm()`:
 *   - кастомный стиль (совпадает с UI-китом),
 *   - не блокирует event loop,
 *   - закрывается на Escape / клик по оверлею,
 *   - Enter подтверждает.
 *
 * Использование:
 *   import { showConfirm } from "@/app/components/ConfirmDialog";
 *   if (await showConfirm("Удалить страницу?")) { ... }
 *   if (await showConfirm({ message: "...", confirmLabel: "Очистить", danger: true })) { ... }
 *
 * Архитектура такая же как у Toast: singleton-компонент в layout.tsx
 * слушает CustomEvent, держит очередь ожидающих Promise'ов и резолвит
 * их после выбора. Работает из любого места без prop drilling.
 */

import { useEffect, useState } from "react";

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** По умолчанию true — красная кнопка для деструктивных действий.
   *  Передать false для «безопасных» подтверждений (синяя кнопка). */
  danger?: boolean;
};

type PendingConfirm = ConfirmOptions & {
  id: number;
  resolve: (v: boolean) => void;
};

const EVENT_NAME = "frogstat:confirm";
let seq = 0;

export function showConfirm(opts: string | ConfirmOptions): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  const options: ConfirmOptions = typeof opts === "string" ? { message: opts } : opts;
  return new Promise<boolean>(resolve => {
    const payload: PendingConfirm = { ...options, id: ++seq, resolve };
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
  });
}

export default function ConfirmDialog() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<PendingConfirm>;
      if (ev.detail) setPending(ev.detail);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { pending.resolve(false); setPending(null); }
      if (e.key === "Enter")  { pending.resolve(true); setPending(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending]);

  if (!pending) return null;

  const answer = (v: boolean) => { pending.resolve(v); setPending(null); };
  const danger = pending.danger !== false;
  const title = pending.title ?? (danger ? "Подтвердите удаление" : "Подтвердите действие");

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center px-4"
      onClick={() => answer(false)}
    >
      <div className="fixed inset-0 bg-black/35 ui-modal-backdrop" />
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#ece8e0] bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)] ui-modal-panel"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="flex items-start gap-3 border-b border-[#ece8e0] bg-[#fbfaf7] px-5 py-4">
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
              danger ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
            }`}
            aria-hidden="true"
          >
            {danger ? "!" : "✓"}
          </span>
          <div className="min-w-0">
            <div id="confirm-dialog-title" className="text-base font-semibold text-gray-900">{title}</div>
            <div className="mt-1 text-xs leading-relaxed text-gray-500">Действие можно отменить только до подтверждения.</div>
          </div>
        </div>
        <div className="px-5 py-4">
          <div className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{pending.message}</div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#ece8e0] bg-white px-5 py-3">
          <button
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-[background-color,transform] hover:bg-gray-50 active:scale-[0.98]"
            onClick={() => answer(false)}
          >
            {pending.cancelLabel ?? "Отмена"}
          </button>
          <button
            className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-[background-color,transform] active:scale-[0.98] ${
              danger ? "bg-red-600 hover:bg-red-700" : "bg-gray-900 hover:bg-gray-700"
            }`}
            onClick={() => answer(true)}
            autoFocus
          >
            {pending.confirmLabel ?? (danger ? "Удалить" : "OK")}
          </button>
        </div>
      </div>
    </div>
  );
}
